import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Category } from 'components/ClubMemberManagement/types';
import { AutoCompleteTextInput } from 'components/Form/AutoCompleteTextInput';
import { CheckboxInput } from 'components/Form/CheckboxInput';
import { SelectInput } from 'components/Form/SelectInput';
import { SubmitInput } from 'components/Form/SubmitInput';
import { TextInput } from 'components/Form/TextInput';
import { WYSIWYGInput } from 'components/Form/WYSIWYGInput';
import { Form } from 'components/Form/base/Form';
import { FormDivider, FormRow } from 'components/Form/next';
import { OutputAssignField } from 'components/IOT/Output/OutputAssignField';
import { refTable, timeDivisions } from 'components/TrainingPlan/TrainingPlanHelpers';
import Flex from 'components/styledComponents/Flex';
import Joi from 'joi';
import { omit } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import useSWR from 'swr';
import { Tooltip } from '~/components/ui';
import { useApi } from '../../../hooks/useApi';
import DeviceManagerInput from '../../AccessControl/Device/DeviceManagerInput';
import { Space } from '../types';

interface Props {
    clubSlug: string;
    space?: Space;
    callback: (space?: Space) => void;
}

export const SpaceForm = ({ space, clubSlug, callback = () => {} }: Props) => {
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);
    const { data } = useSWR(`space/group/${clubSlug}`);
    const { data: categoryData } = useSWR<{ data: Category[] }>(`club-member/${clubSlug}/category`);

    const handleSubmit = (data) => {
        setIsLoading(true);
        return api({
            url: `space/${space?.slug || clubSlug}`,
            method: space ? 'PUT' : 'POST',
            data: {
                ...omit(data, 'form'),
                form_id: data.form?.id,
                booking_guidelines: data.booking_guidelines?.map((g) => g.value),
                repeat_booking_guidelines: data.repeat_booking_guidelines?.map((g) => g.value)
            },
            onBefore: () => setIsLoading(false),
            onSuccess: ({ data }) => callback(data)
        });
    };

    const schema = Joi.object({
        name: Joi.string().required().label('Name'),
        description: Joi.string().allow('', null).label('Beschreibung'),
        space_group_id: Joi.number().min(0).required().label('Gruppe'),
        bookable_until: Joi.number()
            .allow('', null)
            .max(720)
            .min(0)
            .label('Ab wie viel Stunden im voraus darf der Platz gebucht werden?'),
        bookable_in_advance: Joi.number()
            .allow('', null)
            .max(720)
            .min(0)
            .label('Wie viele Stunden im voraus darf der Platz gebucht werden?'),
        // bookable_per_week: Joi.number().allow("", null).max(672).min(0).label("Buchbare Zeiteinheiten pro Woche"),
        max_bookable_in_row: Joi.number()
            .allow('', null)
            .max(96)
            .min(0)
            .label('Maximal buchbare Zeiteinheiten am Stück'),
        min_bookable_in_row: Joi.number()
            .allow('', null)
            .max(96)
            .min(0)
            .label('Minimal buchbare Zeiteinheiten am Stück'),
        anonymous_bookings: Joi.boolean().allow(null).label('Dieses Feld'),
        only_requests: Joi.boolean().allow(null).label('Dieses Feld'),
        repeating_only_requests: Joi.boolean().allow(null).label('Dieses Feld'),
        cancelable_until: Joi.number().allow('', null).max(90).min(0).label('Kündbar im voraus'),
        opening_hours: Joi.array()
            .required()
            .items(
                Joi.object({
                    start: Joi.number().allow(null, '').min(0).max(95).label('Endzeit'),
                    end: Joi.number()
                        .allow(null, '')
                        .min(Joi.ref('start'))
                        .message('***Die Endzeit muss größer sein als die Startzeit')
                        .max(96)
                        .label('Startzeit')
                })
            )
            .min(7)
            .max(7),
        booking_guidelines: Joi.array().allow(null).label('Buchungen erlauben'),
        repeat_booking_guidelines: Joi.array().allow(null).allow(null, '', 0, 1, 2, 3).label('Abo-Buchungen erlauben')
    }).unknown();

    const possibleGuidelines = [
        { value: 'none', label: 'Keiner' },
        { value: 'member', label: 'Mitglieder' },
        { value: 'relative', label: 'Verantwortliche' },
        ...(categoryData?.data || []).map((cat) => ({
            value: `category:${cat.id}`,
            label: `Mitglieder der Kategorie: ${cat.title}`
        }))
    ];

    return (
        <Form
            onSubmit={handleSubmit}
            validationSchema={schema}
            defaultValues={{
                ...space,
                space_group_id: space?.space_group?.id,
                repeat_booking_guidelines: possibleGuidelines.filter((g) =>
                    space?.repeat_booking_guidelines?.includes(g.value)
                ),
                booking_guidelines: possibleGuidelines.filter((g) => space?.booking_guidelines?.includes(g.value))
            }}
        >
            <SelectInput
                name="space_group_id"
                label="Gruppe"
                optionsWithKey={data?.data?.map((group) => ({ id: group.id, label: group.name }))}
            />
            <FormDivider title="Allgemein" />
            <TextInput name="name" label="Titel" />
            <WYSIWYGInput name="description" label="Beschreibung" />
            <FormDivider title="Buchungsregeln" />
            <CheckboxInput className="mb-3" name="anonymous_bookings" label="Namen der Buchenden verstecken?" />
            <CheckboxInput name="only_requests" label="Buchungen nur auf Anfrage" />
            <CheckboxInput name="only_requests_repeating" label="Abo-Buchungen  nur auf Anfrage" />
            <AutoCompleteTextInput
                name="booking_guidelines"
                label="Buchungen erlauben nur für"
                multiple
                dataKey="label"
                data={possibleGuidelines}
            />
            <AutoCompleteTextInput
                name="repeat_booking_guidelines"
                label="Abo-Buchungen erlauben nur für"
                multiple
                dataKey="label"
                data={possibleGuidelines}
            />
            <Flex equal gap={0.5} className="my-3">
                {/* <TextInput
                    type="number"
                    max={672}
                    min={0}
                    name="bookable_per_week"
                    label="Buchbare Zeiteinheiten (15 Minuten) pro Woche pro E-Mail Adresse"
                /> */}
                <TextInput
                    type="number"
                    max={96}
                    min={0}
                    name="max_bookable_in_row"
                    label="Maximal buchbare Zeiteinheiten (15 Minuten) am Stück"
                />
                <TextInput
                    type="number"
                    max={96}
                    min={0}
                    name="min_bookable_in_row"
                    label="Minimal buchbare Zeiteinheiten (15 Minuten) am Stück"
                />
            </Flex>
            <Flex equal gap={0.5} className="my-3">
                <TextInput
                    type="number"
                    max={720}
                    min={0}
                    name="bookable_in_advance"
                    label={
                        <>
                            Ab wann darf der Platz gebucht werden?{' '}
                            <Tooltip tooltip="Darf der Platz für Mittwoch schon am Sonntag oder erst am Montag gebucht werden? Entscheide du! Trage die Vorlaufzeit in Stunden an ( 1 Tag = 24 Stunden ).">
                                <FontAwesomeIcon icon={faQuestionCircle} />
                            </Tooltip>
                        </>
                    }
                />
                <TextInput
                    type="number"
                    max={720}
                    min={0}
                    name="bookable_until"
                    label={
                        <>
                            Bis wann darf darf der Platz gebucht werden?{' '}
                            <Tooltip tooltip="Darf der Platz für 16 Uhr spätestens um 9 Uhr morgens gebucht werden oder vielleicht sogar bis 15 Uhr? Trage die Zeit ein, bis wie viele Stunden vor Buchungsbeginn der Platz gebucht werden darf, wenn er frei ist.">
                                <FontAwesomeIcon icon={faQuestionCircle} />
                            </Tooltip>
                        </>
                    }
                />
            </Flex>
            <TextInput type="number" max={90} min={0} name="cancelable_until" label="Kündbar im voraus (in Stunden)" />
            <AutoCompleteTextInput
                name="starting_point"
                label="Startzeitpunkt (Bei keinem Wert ist jeder Zeitpunkt möglich)"
                multiple
                minimizedDataKey="value"
                data={[
                    {
                        value: 0,
                        label: 'Zur vollen Stunde'
                    },
                    {
                        value: 1,
                        label: 'Viertel nach'
                    },
                    {
                        value: 2,
                        label: 'Zur halben Stunde'
                    },
                    {
                        value: 3,
                        label: 'Viertel vor'
                    }
                ]}
                dataKey="label"
            />
            <FormRow>
                <TextInput
                    type="number"
                    min={0}
                    name="open_booking_per_email.max_bookings"
                    label="Maximale offene Buchungen pro E-Mail Adresse / Benutzer"
                />
                <TextInput
                    type="number"
                    min={0}
                    name="open_booking_per_email.max_slots"
                    label="Maximale offene gebuchte Slots pro E-Mail Adresse / Benutzer"
                />
            </FormRow>
            <TextInput type="number" min={0} name="max_bookings_per_day" label="Maximale offene Buchungen pro Tag" />
            <CheckboxInput
                name="open_booking_per_email.include_guests"
                label="Buchungen wo Spieler als Gast eingetragen ist mit einbeziehen"
            />
            <CheckboxInput name="open_booking_per_email.disallow_guest" label="Eintragungen als Gast mit begrenzen" />
            <FormDivider title="Öffnungszeiten" />
            <OpeningHoursInput />
            <FormDivider title="Platzsteuerung zuweisen" />
            <OutputAssignField />
            <DeviceManagerInput />
            <SubmitInput block isLoading={isLoading}>
                Speichern
            </SubmitInput>
        </Form>
    );
};

const StyledOpeningHoursInputGrid = styled.div`
    display: grid;
    grid-template-columns: 0.6fr 1fr 1fr;
    grid-gap: 0.25rem;
    margin-bottom: 1rem;
    align-items: center;

    > span:nth-child(-n + 3) {
        justify-self: center;
    }
`;

const OpeningHoursInput = () => {
    const { setValue } = useFormContext();
    const times = timeDivisions.times.map((time, index) => ({ value: index, label: time }));

    const setOpeningHours = useCallback(
        (e: any, key: any) => {
            if (confirm('Bist du sicher, dass du deine bisherigen Öffnungszeiten überschreiben möchtest?'))
                timeDivisions.days.forEach((_, index) => setValue(`opening_hours.${index}.${key}`, e.target.value));
        },
        [setValue]
    );

    return (
        <>
            <h5 className="mb-3">Öffnungszeiten einstellen</h5>
            <StyledOpeningHoursInputGrid>
                <span>Alle Wochentage</span>
                <select className="form-control" onChange={(e) => setOpeningHours(e, 'start')}>
                    {times.map((time) => (
                        <option key={time.value} value={time.value}>
                            {time.label}
                        </option>
                    ))}
                </select>
                <select className="form-control" onChange={(e) => setOpeningHours(e, 'end')}>
                    {times.map((time) => (
                        <option key={time.value} value={time.value}>
                            {time.label}
                        </option>
                    ))}
                </select>
            </StyledOpeningHoursInputGrid>
            <StyledOpeningHoursInputGrid>
                <span />
                <span>von</span>
                <span>bis</span>
                {refTable.days.map((day, index) => (
                    <React.Fragment key={day}>
                        <span>{day}</span>
                        <SelectInput name={`opening_hours.${index}.start`} className="mb-0" optionsWithKey={times} />
                        <SelectInput name={`opening_hours.${index}.end`} className="mb-0" optionsWithKey={times} />
                    </React.Fragment>
                ))}
            </StyledOpeningHoursInputGrid>
        </>
    );
};
